import React, { useState, useEffect, useCallback } from "react";
import { Skeleton } from "@mui/material";
import DateConverter from "@heartinz/date_and_time_processor";

import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import { XIcon } from "../../../constants/Icons";

const StockTrack = ({ isData, onClose }) => {
    const [isPreLoading, setPreLoading] = useState(true);
    const [isTrackData, setTrackData] = useState(true);

    const { checkNetwork } = useNetwork();
    const showAlert = useShowAlert();
    const handleTrack = useCallback(async () => {
        setPreLoading(true);
        try {
            if (!checkNetwork()) return;
            const data = {
                stockid: isData._id,
                stocklid: isData.stockLinkId,
                productid: isData._id,
            };
            const response = await AxiosInstance.get(`module/stock/${isData.productid}`);
            setTrackData(response.data.results);
        } catch (error) {
            handleError(error, showAlert);
            return false;
        } finally {
            setPreLoading(false);
        }
    }, [checkNetwork, isData, showAlert]);

    useEffect(() => {
        handleTrack();
    }, [handleTrack]);

    const getColor = (status) => {
        if (!status || typeof status !== "string") return "bg-label-secondary";
        switch (status.toLowerCase()) {
            case "added":
            case "stockin":
            case "returned":
            case "cancelled":
                return "bg-label-plus";
            case "removed":
            case "stockout":
            case "transfered":
            case "trashed":
            case "sale":
                return "bg-label-minus";
            default:
                return "bg-label-secondary";
        }
    };

    const getTextColor = (status) => {
        if (!status || typeof status !== "string") return "text-secondary";
        switch (status.toLowerCase()) {
            case "added":
            case "stockin":
            case "returned":
            case "cancelled":
                return "text-plus";
            case "removed":
            case "sale":
            case "stockout":
            case "transfered":
            case "trashed":
                return "text-minus";
            default:
                return "text-secondary";
        }
    };

    const formatTrackMode = (trackmode) => {
        switch (trackmode?.toUpperCase()) {
            case "ADDED":
                return "Stock In";
            case "STOCKIN":
                return "Stock In";
            case "RETURNED":
                return "Stock In";
            case "CANCELLED":
                return "Stock In";
            case "REMOVED":
                return "Stock Out";
            case "STOCKOUT":
                return "Stock Out";
            case "TRANSFERED":
                return "Stock Out";
            case "SALE":
                return "Stock Out";
            case "TRASHED":
                return "Stock Out";
            default:
                return trackmode;
        }
    };

    return (
        <div>
            <div className="modal-backdrop show"></div>
            <div className="modal show" id="CustomerModal" tabIndex="0" aria-modal="true" role="dialog" style={{ display: "block" }}>
                <div className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-xl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h6 className="modal-title">Stock History</h6>
                            <button type="button" onClick={onClose} className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body p-0">
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <thead className="table-light text-center">
                                        <tr>
                                            <th>S.No</th>
                                            <th>Date</th>
                                            <th>Mode</th>
                                            <th>Reason</th>
                                            <th>Stock Before</th>
                                            <th>Adjustment</th>
                                            <th>Stock After</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isPreLoading ? (
                                            [...Array(6)].map((_, index) => (
                                                <tr key={index}>
                                                    <td className="text-center"><Skeleton variant="text" width={50} /></td>
                                                    <td className="text-center"><Skeleton variant="text" width={180} /></td>
                                                    <td className="text-center"><Skeleton variant="text" width={100} /></td>
                                                    <td className="text-center"><Skeleton variant="text" width={200} /></td>
                                                    <td className="text-center"><Skeleton variant="text" width={150} /></td>
                                                    <td className="text-center"><Skeleton variant="text" width={150} /></td>
                                                    <td className="text-center"><Skeleton variant="text" width={150} /></td>
                                                </tr>
                                            ))
                                        ) : Array.isArray(isTrackData) && isTrackData.length > 0 ? (
                                            isTrackData.map((item, index) => (
                                                <tr key={item.id || index}>
                                                    <td className="text-center">{isTrackData.length - index}</td>
                                                    <td className="text-center">
                                                        {DateConverter.ISOToDisplayFormat(item.createdAt).date} - {DateConverter.ISOToDisplayFormat(item.createdAt).time}
                                                    </td>
                                                    <td className="text-center">
                                                        {item.trackmode && (
                                                            <span className={`badge rounded-pill ${getColor(item.trackmode)}`}>
                                                                {formatTrackMode(item.trackmode)}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <small className="text-truncate">{item.tracknote}</small>
                                                    </td>
                                                    <td className="text-center">{item.stockBefore}</td>
                                                    <td className="text-center">
                                                        {item.trackmode && (
                                                            <div className={`${getTextColor(item.trackmode)}`}>
                                                                {['STOCKIN', 'ADDED', 'RETURNED', 'CANCELLED'].includes(item.trackmode) ? (
                                                                    <div>+ {item.quantity}</div>
                                                                ) : ['STOCKOUT', 'REMOVED', 'TRANSFERED', 'SALE', 'TRASHED'].includes(item.trackmode) ? (
                                                                    <div>- {item.quantity}</div>
                                                                ) : null}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="text-center fw-500">{item.stockAfter}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center p-2">
                                                    <span className="text-muted">No stock track found in the store. Add some stock to get started.</span>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer d-block">
                            <button onClick={onClose} type="button" data-bs-dismiss="modal" className="btn btn-outline-secondary">
                                <XIcon/> Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StockTrack;